import { useDrilldownSchema } from 'features/drilldown'
import { useGetPrecedingFilters } from 'features/drilldown/data/DrilldownFilterContext'
import { FieldDef } from 'features/drilldown/rolodex/DrilldownDataSchema'
import {
  DrilldownField,
  DrilldownRolodexType,
} from 'features/drilldown/rolodex/ontology/ontologyTypes'
import { useGeckoNotifications } from 'features/notifications'
import {
  RolodexEntryQueryBody,
  useRolodexEntries,
  useRolodexHistogram,
} from 'features/rolodex'
import _ from 'lodash'
import { useMemo } from 'react'
import {
  GeckoEntry,
  RolodexEntryType,
  RolodexHistogramAggregationOp,
  RolodexHistogramFilter,
  RolodexHistogramQuery,
  RolodexHistogramResponse,
} from 'types'

import { useRolodexIds } from './useRolodexIds'

const RD_QUERY_LIMIT = 1000000
const FALLBACK_QUERY_TYPE = 'organization'

export type DrilldownHistogram = Record<string, number>

type UseInflectedQueryProps = {
  select: RolodexHistogramQuery['select']
  filters: readonly RolodexHistogramFilter[]
  queryType?: DrilldownRolodexType
}

type UseInflectedQueryResult = {
  filters: readonly RolodexHistogramFilter[]
  tags: RolodexHistogramQuery['tags'] | null
  type: DrilldownRolodexType
}

/**
 * If a query is on a particular type, and requires filters from a type which
 * is not an ancestor, we can attempt to rectify this by applying the
 * incompatible filters to get the relevant IDs of some common ancestor,
 * and then filtering to only that common ancestor.
 *
 * For example, I want to count the number of indications by site only
 * for inspections in the last year:
 *   - Get the unit IDs of inspections in the last year
 *   - Do the indication query based tagged to the units from those inspections
 *
 * This function will generate that tag set, and return only the filters which
 * applied to the types in that same 'branch' as your select query.
 *
 * Constraint: all of the terms in your select query must be in the same branch.
 */
function useInflectedQueries(
  props: UseInflectedQueryProps | ReadonlyArray<UseInflectedQueryProps>
): {
  queryResults: ReadonlyArray<UseInflectedQueryResult>
  isLoading: boolean
} {
  const propsArray = useMemo<ReadonlyArray<UseInflectedQueryProps>>(
    () => (Array.isArray(props) ? props : [props]),
    [props]
  )

  const { errorNotification } = useGeckoNotifications()
  const { bucketLinkedTypes, getInflectionType, findLowestType } =
    useDrilldownSchema()

  const bucketCalculations = useMemo(() => {
    return propsArray.map(({ select, filters, queryType }) => {
      const selectTypes = select
        .map((s) => s.type as DrilldownRolodexType)
        .filter((item): item is NonNullable<typeof item> => !!item)
      const filterTypes = filters
        .map((f) => f.type as DrilldownRolodexType)
        .filter((item): item is NonNullable<typeof item> => !!item)
      const allTypes = _.uniq(
        [...selectTypes, ...filterTypes, queryType].filter(
          (item): item is NonNullable<typeof item> => !!item
        )
      )
      const bucketedTypes = bucketLinkedTypes(allTypes)

      if (bucketedTypes.length <= 1) {
        return { selectBucket: allTypes, reduceBuckets: [] }
      }

      const selectBucketIdx = _.findIndex(bucketedTypes, (bucket) =>
        selectTypes.every((sT) => bucket.includes(sT))
      )

      if (selectBucketIdx < 0) {
        errorNotification(
          `Something went wrong - couldn't find a single bucket with the select terms`
        )
        return { selectBucket: allTypes, reduceBuckets: [] }
      }

      const reduceBuckets = bucketedTypes.filter(
        (_, idx) => idx !== selectBucketIdx
      )

      return { selectBucket: bucketedTypes[selectBucketIdx], reduceBuckets }
    })
  }, [propsArray, bucketLinkedTypes, errorNotification])

  const bucketQueries = useMemo(() => {
    return bucketCalculations.flatMap(
      ({ selectBucket, reduceBuckets }, idx) => {
        const { filters } = propsArray[idx]

        return reduceBuckets
          .map((reduceBucket) => {
            const inflectionType = getInflectionType(selectBucket, reduceBucket)
            if (!inflectionType) {
              errorNotification(`No inflection type for ${inflectionType}`)
              return null
            }

            const filterRelevantTypes = [...reduceBucket, inflectionType]
            const lowestType = findLowestType(filterRelevantTypes)

            return lowestType
              ? {
                  select: [
                    {
                      type: inflectionType as RolodexEntryType,
                      field: 'id',
                      group_by: true,
                      label: 'inflection_id',
                    },
                  ],
                  filters: filters.filter((filter) =>
                    filterRelevantTypes.includes(
                      filter.type as DrilldownRolodexType
                    )
                  ),
                  links: _.uniq(
                    filterRelevantTypes.filter((t) => t !== lowestType)
                  ) as RolodexEntryType[],
                  type: lowestType as RolodexEntryType,
                  tags: {},
                  limit: RD_QUERY_LIMIT,
                }
              : null
          })
          .filter((item): item is NonNullable<typeof item> => !!item)
      }
    )
  }, [
    bucketCalculations,
    propsArray,
    getInflectionType,
    findLowestType,
    errorNotification,
  ])

  const { data: bucketResults, isLoading } = useRolodexHistogram(bucketQueries)

  const queryResults = useMemo(() => {
    let bucketQueryCursor = 0

    return propsArray.map(({ filters, queryType }, idx) => {
      const { selectBucket, reduceBuckets } = bucketCalculations[idx]

      const currentBucketQueries = bucketQueries.slice(
        bucketQueryCursor,
        bucketQueryCursor + reduceBuckets.length
      )
      const currentBucketResults = bucketResults?.slice(
        bucketQueryCursor,
        bucketQueryCursor + reduceBuckets.length
      )
      bucketQueryCursor += reduceBuckets.length

      const inflectionTags: RolodexHistogramQuery['tags'] | null =
        currentBucketQueries.length && currentBucketResults
          ? (() => {
              const bucketInflectionIdSets = currentBucketResults.map(
                (result) =>
                  new Set(
                    result
                      .map((row) => row['inflection_id'] as string | null)
                      .filter((item): item is string => !!item)
                  )
              )

              const bucketInflectionTypes = currentBucketQueries.map(
                (query) => query.select[0].type! as DrilldownRolodexType
              )

              const tagSets = _.reduce(
                bucketInflectionTypes,
                (accum, currType, index) => {
                  return {
                    ...accum,
                    [currType]: accum[currType].intersection(
                      bucketInflectionIdSets[index]
                    ),
                  }
                },
                Object.fromEntries(
                  bucketInflectionTypes.map((t, idx) => [
                    t,
                    new Set(bucketInflectionIdSets[idx] ?? []),
                  ])
                )
              )

              return Object.fromEntries(
                Object.entries(tagSets).map(([type, tagSet]) => [
                  type,
                  [...tagSet],
                ])
              )
            })()
          : null

      return {
        filters: filters.filter(
          (f) =>
            !!f.type && selectBucket.includes(f.type as DrilldownRolodexType)
        ),
        tags: inflectionTags
          ? Object.fromEntries(
              Object.entries(inflectionTags).map(([type, values]) => [
                type,
                values.length ? values : ['no-op'],
              ])
            )
          : null,
        type: findLowestType(selectBucket) ?? queryType ?? FALLBACK_QUERY_TYPE,
      }
    })
  }, [
    propsArray,
    bucketCalculations,
    bucketQueries,
    bucketResults,
    findLowestType,
  ])

  return useMemo(() => ({ queryResults, isLoading }), [queryResults, isLoading])
}

export type UseHistogramQueryProps = {
  select: RolodexHistogramQuery['select']
  filters: readonly RolodexHistogramFilter[]
  queryType?: DrilldownRolodexType
}

export function useHistogramQueries(
  props: UseHistogramQueryProps | ReadonlyArray<UseHistogramQueryProps>
): {
  isLoading: boolean
  queries: ReadonlyArray<RolodexHistogramQuery>
} {
  // Normalize props to a memoized ReadonlyArray
  const propsArray: ReadonlyArray<UseHistogramQueryProps> = useMemo(
    () => (Array.isArray(props) ? props : [props]),
    [props]
  )

  // Call useInflectedQueries with the normalized array
  const { isLoading, queryResults } = useInflectedQueries(
    propsArray.map(({ select, filters, queryType }) => ({
      select,
      filters,
      queryType,
    }))
  )

  // Memoize the queries array
  const queries: ReadonlyArray<RolodexHistogramQuery> = useMemo(() => {
    return queryResults.map(({ filters, tags, type }, index) => {
      const { select } = propsArray[index]

      const filterTypes = filters.map((f) => f.type ?? FALLBACK_QUERY_TYPE)
      const selectTypes = select.map((s) => s.type ?? FALLBACK_QUERY_TYPE)
      const links = _.uniq(
        [...filterTypes, ...selectTypes].filter((t) => t !== type)
      ) as RolodexEntryType[]

      return {
        type: type as RolodexEntryType,
        links,
        filters: [...filters],
        select,
        tags: tags ?? {},
        limit: RD_QUERY_LIMIT,
      }
    })
  }, [queryResults, propsArray])

  // Memoize the returned object
  return useMemo(
    () => ({
      queries,
      isLoading,
    }),
    [queries, isLoading]
  )
}

type RolodexHistogramCast = RolodexHistogramQuery['select'][number]['cast']
export function histogramSelect(
  groupingType: DrilldownRolodexType,
  aggregationType: DrilldownRolodexType,
  groupingColumn: string = 'id',
  aggregationColumn: string = 'id',
  histogramGroupByPropertyKey: 'key' | 'field' = 'field',
  aggregationPropertyKey: 'key' | 'field' = 'field',
  op: RolodexHistogramAggregationOp = 'count',
  aggCast?: RolodexHistogramCast
): RolodexHistogramQuery['select'] {
  return [
    {
      [histogramGroupByPropertyKey]: groupingColumn,
      type: groupingType,
      group_by: true,
      label: 'group_id',
      order_by: true,
    },
    {
      [aggregationPropertyKey]: aggregationColumn,
      type: aggregationType,
      op,
      label: 'value',
      cast: aggCast,
    },
  ]
}

export function extractHistoResult(
  histoResponse: RolodexHistogramResponse | undefined
): DrilldownHistogram {
  const histoData = histoResponse ?? []
  return Object.fromEntries(
    histoData
      .map((histoResultEntry): [string | undefined, number] => {
        const id = histoResultEntry['group_id'] as string | undefined
        const value = histoResultEntry['value'] as number
        return [id, value]
      })
      .filter(([id]) => !_.isNil(id))
      // Naive handling of invalid response values due to bad data
      // 1960s, 1990s, no dashes, etc
      .filter(([id]) => {
        const idAsString = id
        return !(
          idAsString === '20250206' ||
          _.startsWith(idAsString, '196') ||
          _.startsWith(idAsString, '199') ||
          _.startsWith(idAsString, '2010') ||
          _.startsWith(idAsString, '2011') ||
          _.startsWith(idAsString, '2012') ||
          _.startsWith(idAsString, '2013') ||
          _.startsWith(idAsString, '2014') ||
          _.startsWith(idAsString, '2015') ||
          _.startsWith(idAsString, '203')
        )
      })
  )
}

export function useFilteredEntryQuery(type: DrilldownRolodexType): {
  query: RolodexEntryQueryBody
  isLoading: boolean
} {
  const {
    queries: [rdHistoQuery],
    isLoading,
  } = useHistogramQueries({
    select: [{ type }],
    filters: useGetPrecedingFilters(),
    queryType: type,
  })
  return useMemo(
    () => ({
      query: {
        type: rdHistoQuery.type,
        links: rdHistoQuery.links,
        filters: rdHistoQuery.filters,
        tags: rdHistoQuery.tags,
        limit: rdHistoQuery.limit,
      },
      isLoading,
    }),
    [
      isLoading,
      rdHistoQuery.filters,
      rdHistoQuery.limit,
      rdHistoQuery.links,
      rdHistoQuery.tags,
      rdHistoQuery.type,
    ]
  )
}

export function useFilteredIds(
  type: DrilldownRolodexType,
  limit?: number
): {
  ids: ReadonlyArray<string>
  isLoading: boolean
} {
  const { query, isLoading: queryLoading } = useFilteredEntryQuery(type)
  const histoQuery = useMemo<ReadonlyArray<RolodexHistogramQuery>>(
    () =>
      query
        ? [
            {
              ...(query as RolodexHistogramQuery),
              select: [{ type, field: 'id', label: 'id' }],
              limit: limit ?? RD_QUERY_LIMIT,
            },
          ]
        : [],
    [limit, query, type]
  )
  const { data, isLoading: resultLoading } = useRolodexHistogram(histoQuery)
  return useMemo(
    () => ({
      ids: _.flatten(data).map((d) => d['id'] as string),
      isLoading: queryLoading || resultLoading,
    }),
    [data, queryLoading, resultLoading]
  )
}

/**
 * Helper to get a count of how many entries of a type are in scope given current filters
 *
 * @param type - The type of entry to count
 * @param additionalFilters - Additional filters to apply in addition to the ones from above the panel
 * @returns An object containing the count and a loading state
 */
export function useFilteredEntryCount(
  type: DrilldownRolodexType,
  additionalFilters?: ReadonlyArray<RolodexHistogramFilter>
): {
  count: number
  isLoading: boolean
} {
  const {
    histograms: [histogram],
    isLoading,
  } = useDrilldownHistograms({
    groupingType: type,
    aggregationType: type,
    groupingColumn: 'id',
    aggregationColumn: 'id',
    histogramGroupByPropertyKey: 'field',
    filters: additionalFilters,
  })

  const count = useMemo(() => Object.keys(histogram).length, [histogram])

  return useMemo(
    () => ({
      count,
      isLoading,
    }),
    [count, isLoading]
  )
}

export type UseHistogramsProps = {
  groupingType: DrilldownRolodexType
  aggregationType: DrilldownRolodexType
  groupingColumn?: string
  aggregationColumn?: string
  histogramGroupByPropertyKey?: 'key' | 'field'
  aggregationPropertyKey?: 'key' | 'field'
  filters?: ReadonlyArray<RolodexHistogramFilter>
  op?: RolodexHistogramAggregationOp
  aggCast?: RolodexHistogramCast
}

export function useDrilldownHistograms(
  props: UseHistogramsProps | ReadonlyArray<UseHistogramsProps>
): {
  isLoading: boolean
  histograms: ReadonlyArray<DrilldownHistogram>
} {
  // Normalize props to a memoized ReadonlyArray
  const propsArray: ReadonlyArray<UseHistogramsProps> = useMemo(
    () => (Array.isArray(props) ? props : [props]),
    [props]
  )

  const precedingFilters = useGetPrecedingFilters()

  // Build select arrays for each prop set
  const selects = useMemo(
    () =>
      propsArray.map(
        ({
          groupingType,
          aggregationType,
          groupingColumn = 'id',
          aggregationColumn = 'id',
          histogramGroupByPropertyKey = 'field',
          aggregationPropertyKey = 'field',
          op = 'count',
          aggCast,
        }) =>
          histogramSelect(
            groupingType,
            aggregationType,
            groupingColumn,
            aggregationColumn,
            histogramGroupByPropertyKey,
            aggregationPropertyKey,
            op,
            aggCast
          )
      ),
    [propsArray]
  )

  // Build filters arrays for each prop set
  const filtersArray = useMemo(
    () =>
      propsArray.map(({ filters = [] }) => [...precedingFilters, ...filters]),
    [propsArray, precedingFilters]
  )

  // Call useHistogramQueries with selects and filters
  const { queries: rdHistoQueries, isLoading: queriesLoading } =
    useHistogramQueries(
      useMemo(
        () =>
          selects.map((select, index) => ({
            select,
            filters: filtersArray[index],
          })),
        [selects, filtersArray]
      )
    )

  // Fetch histogram data for each query
  const { data: dataArrays, isLoading: dataLoading } =
    useRolodexHistogram(rdHistoQueries)

  // Extract counts from each data array, safely handling undefined dataArrays
  const histograms = useMemo(
    () =>
      rdHistoQueries.map((_, index) => {
        const data = dataArrays?.[index]
        return data ? extractHistoResult(data) : {}
      }),
    [dataArrays, rdHistoQueries]
  )

  // Combine loading states into a single flag
  const isLoading = queriesLoading || dataLoading

  // Return the single isLoading flag separately
  return useMemo(
    () => ({
      isLoading,
      histograms,
    }),
    [isLoading, histograms]
  )
}

export type DrilldownPaginationType = {
  pageSize: number
  current: number
}

function optionalAddToArrayAndUniq<T>(
  array: ReadonlyArray<T> | undefined,
  item: T | undefined
): T[] {
  return _.uniq([...(array ?? []), ...(item ? [item] : [])])
}

export function usePaginatedQuery(
  query: RolodexEntryQueryBody = {},
  pagination: DrilldownPaginationType,
  groupByType?: DrilldownRolodexType
): {
  query: RolodexEntryQueryBody | undefined
  isLoading: boolean
  totalEntries: number
  sorter: (a: GeckoEntry, b: GeckoEntry) => number
} {
  const queryWithGroup = useMemo(
    () => ({
      ...query,
      links: optionalAddToArrayAndUniq(query.links, groupByType).filter(
        (t) => !!t && t !== query.type
      ),
    }),
    [groupByType, query]
  )

  const { data: groupingData, isLoading: groupingLoading } =
    useRolodexHistogram(
      useMemo(
        () => ({
          ...queryWithGroup,
          filters: queryWithGroup.filters as
            | undefined
            | RolodexHistogramFilter[],
          select: [
            {
              field: 'id',
              label: 'baseId',
              group_by: true,
            },
            {
              field:
                groupByType === queryWithGroup.type
                  ? 'id'
                  : `tags.${groupByType}`,
              label: 'groupById',
              group_by: true,
            },
          ],
          links: optionalAddToArrayAndUniq(
            queryWithGroup?.links,
            groupByType
          ).filter((t) => t !== queryWithGroup.type) as RolodexEntryType[],
          type: queryWithGroup.type,
          limit: RD_QUERY_LIMIT,
        }),
        [groupByType, queryWithGroup]
      )
    )

  const groupIdToBaseId = useMemo<
    ReadonlyMap<string, ReadonlyArray<string>>
  >(() => {
    const groupingDataRecs: ReadonlyArray<{ id: string; groupById: string }> =
      _.flatten(groupingData ?? [])
        .map((rec) => ({
          id: rec['baseId'] as string,
          groupById: rec['groupById'] as string,
        }))
        .filter(
          (item): item is NonNullable<typeof item> =>
            !_.isNil(item) && !_.isNil(item.id) && !_.isNil(item.groupById)
        )

    const groupIdToOrderedId = new Map<string, string[]>()
    groupingDataRecs.forEach((rec) => {
      const arr = groupIdToOrderedId.get(rec.groupById) ?? []
      groupIdToOrderedId.set(rec.groupById, arr)
      arr.push(rec.id)
    })
    return groupIdToOrderedId
  }, [groupingData])

  const orderBy = query.order_by
  const { ids: orderedGroupIds, isLoading: orderedGroupIdsLoading } =
    useRolodexIds(
      useMemo(() => {
        const ids = [...groupIdToBaseId.keys()]
        return ids.length
          ? [
              {
                ids,
                type: groupByType as RolodexEntryType,
                order_by: orderBy,
              },
            ]
          : []
      }, [groupByType, groupIdToBaseId, orderBy])
    )

  const entryQuery = useMemo<RolodexEntryQueryBody | undefined>(() => {
    const pageGroups = _.chunk(orderedGroupIds, pagination?.pageSize ?? 10)
    const pages = pageGroups.map((groups) =>
      _.flatMap(groups, (group) => groupIdToBaseId.get(group) ?? [])
    )
    const currPage = (pagination?.current ?? 1) - 1
    const thisPageIds = (currPage < pages.length ? pages[currPage] : []) ?? []

    const links = _.uniq([
      ...optionalAddToArrayAndUniq(queryWithGroup?.links, groupByType),
    ]) as RolodexEntryType[]
    return {
      ids: [...thisPageIds].filter((id) => !_.isNil(id)),
      links,
      order_by: orderBy,
    }
  }, [
    groupByType,
    groupIdToBaseId,
    orderBy,
    orderedGroupIds,
    pagination,
    queryWithGroup?.links,
  ])

  // Use this position map to avoid an n^2 sorting on the table every
  // time. Lazily loaded in case we don't need it.
  const groupIdPositions = useMemo<() => ReadonlyMap<string, number>>(
    () =>
      _.memoize(() => new Map(orderedGroupIds?.map((id, idx) => [id, idx]))),
    [orderedGroupIds]
  )

  return useMemo(
    () => ({
      query: entryQuery,
      isLoading: orderedGroupIdsLoading || groupingLoading,
      totalEntries: orderedGroupIds?.length ?? 0,
      sorter: (a, b) => {
        const posA = groupIdPositions().get(a.id) ?? Number.MAX_VALUE
        const posB = groupIdPositions().get(b.id) ?? Number.MAX_VALUE
        return posA - posB
      },
    }),
    [
      entryQuery,
      groupIdPositions,
      groupingLoading,
      orderedGroupIds?.length,
      orderedGroupIdsLoading,
    ]
  )
}

/**
 * Helper function to get linked IDs from a set of IDs - for example,
 * given a list of inspection IDs, what are the units they're linked to?
 */
export function useGetLinkedIds(
  ids: ReadonlyArray<string>,
  toType: DrilldownRolodexType
): { links: ReadonlyMap<string, string>; isLoading: boolean } {
  const { entries, isLoading } = useRolodexEntries(
    useMemo(
      () =>
        ids.length
          ? [
              {
                ids: [...ids],
              },
            ]
          : [],
      [ids]
    )
  )

  return useMemo(() => {
    const recs = entries
      .map((e) => ({
        from: e.id,
        to:
          toType === e.type
            ? e.id
            : (e.tags[toType as RolodexEntryType] as string | undefined),
      }))
      .filter((e) => !!e.to)
    const resultMap = new Map<string, string>()
    recs.forEach(({ from, to }) => resultMap.set(from, to as string))
    return { links: resultMap, isLoading }
  }, [entries, isLoading, toType])
}

export function useFieldValues(field: DrilldownField): {
  isLoading: boolean
  values: ReadonlyArray<string>
} {
  const { fields } = useDrilldownSchema()

  const { key, entryType: type, keyOrField } = fields?.[field] ?? {}
  const precedingFilters = useGetPrecedingFilters()
  const select = useMemo<RolodexHistogramQuery['select']>(
    () => [
      {
        type,
        [keyOrField]: key,
        label: 'value',
        group_by: true,
      },
    ],
    [key, keyOrField, type]
  )

  const {
    queries: [query],
    isLoading: isHistoQueryLoading,
  } = useHistogramQueries({
    select,
    filters: precedingFilters,
  })
  const { data, isLoading } = useRolodexHistogram(query)

  return useMemo(
    () => ({
      values: data?.flat().map((e) => e['value'] as string) ?? [],
      isLoading: isLoading || isHistoQueryLoading,
    }),
    [data, isHistoQueryLoading, isLoading]
  )
}

export function useFieldKeys(
  fields: ReadonlyArray<string>,
  filter?: (fieldDef: FieldDef) => boolean
): Readonly<Record<string, { field?: string; key?: string; type?: string }>> {
  const { fields: schemaFields } = useDrilldownSchema()
  return useMemo<
    Readonly<Record<string, { field?: string; key?: string; type?: string }>>
  >(
    () =>
      _.reduce(
        fields,
        (acc, field) => {
          const fieldDef = schemaFields?.[field]
          if (!fieldDef || (filter && !filter(fieldDef))) {
            return acc
          }
          const { keyOrField, key: fieldKey, entryType } = fieldDef
          return {
            ...acc,
            [field]: {
              [keyOrField === 'field' ? 'field' : 'key']: fieldKey,
              type: entryType,
            },
          }
        },
        {}
      ),
    [fields, filter, schemaFields]
  )
}
